import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface BuzzKeyword {
  keyword: string;
  count: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  context: string;
}

export interface BuzzTheme {
  theme: string;
  description: string;
  articles: number;
  sentiment: 'positive' | 'neutral' | 'negative';
}

export interface BuzzSource {
  name: string;
  count: number;
}

export interface BuzzHeadline {
  title: string;
  url: string;
  source: string;
  sentiment: 'positive' | 'neutral' | 'negative';
}

export interface AthleteBuzz {
  keywords: BuzzKeyword[];
  themes: BuzzTheme[];
  topSources: BuzzSource[];
  headlines: BuzzHeadline[];
  totalArticles: number;
  dateRange: string;
  lastUpdated: Date;
}

interface CacheEntry {
  data: AthleteBuzz;
  timestamp: number;
}

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

export function useAthleteBuzz() {
  const [buzzData, setBuzzData] = useState<AthleteBuzz | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cache = useRef<Map<string, CacheEntry>>(new Map());

  const fetchBuzz = useCallback(async (
    athleteId: string,
    athleteName: string,
    teamName?: string,
    sport?: string,
    dateRange: '7d' | '30d' | '90d' = '7d',
    forceRefresh = false
  ): Promise<AthleteBuzz | null> => {
    const cacheKey = `${athleteId}-${dateRange}`;
    
    // Check cache
    if (!forceRefresh) {
      const cached = cache.current.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setBuzzData(cached.data);
        return cached.data;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      // First, get search results from Google
      const { data: searchData, error: searchError } = await supabase.functions.invoke('google-search-athlete', {
        body: {
          athleteName,
          teamName,
          sport,
          dateRange,
        },
      });

      if (searchError) {
        throw new Error(searchError.message || 'Failed to fetch search results');
      }

      if (!searchData?.searchResults || searchData.searchResults.length === 0) {
        const emptyBuzz: AthleteBuzz = {
          keywords: [],
          themes: [],
          topSources: [],
          headlines: [],
          totalArticles: 0,
          dateRange,
          lastUpdated: new Date(),
        };
        setBuzzData(emptyBuzz);
        return emptyBuzz;
      }

      // Analyze the search results for buzz
      const { data: buzzAnalysis, error: buzzError } = await supabase.functions.invoke('analyze-athlete-buzz', {
        body: {
          searchResults: searchData.searchResults,
          athleteName,
          dateRange,
        },
      });

      if (buzzError) {
        throw new Error(buzzError.message || 'Failed to analyze buzz');
      }

      const buzz: AthleteBuzz = {
        keywords: buzzAnalysis?.keywords || [],
        themes: buzzAnalysis?.themes || [],
        topSources: buzzAnalysis?.topSources || [],
        headlines: buzzAnalysis?.headlines || [],
        totalArticles: searchData.searchResults.length,
        dateRange,
        lastUpdated: new Date(),
      };

      // Cache the result
      cache.current.set(cacheKey, { data: buzz, timestamp: Date.now() });
      setBuzzData(buzz);
      return buzz;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch buzz data';
      setError(message);
      console.error('Error fetching athlete buzz:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearCache = useCallback((athleteId?: string) => {
    if (athleteId) {
      // Clear specific athlete cache for all date ranges
      ['7d', '30d', '90d'].forEach(range => {
        cache.current.delete(`${athleteId}-${range}`);
      });
    } else {
      cache.current.clear();
    }
  }, []);

  return {
    buzzData,
    isLoading,
    error,
    fetchBuzz,
    clearCache,
  };
}
